import { ConditionsNew } from '@retail/calculus/types';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { useDisclosure } from '@shared/hooks';
import { useState } from 'react';
import { Stack } from '@mui/material';
import { AddButton, CalculusActionPortal, DeleteDialog } from '@retail/components';
import { OutlinedButton } from '@shared/components';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import CreateCustomerMarkupConditionDialog from './Create/CreateCustomerMarkupConditionDialog';
import { CustomerMarkupTable } from '@retail/customer-markup/components';
import { useConditionActions } from '@retail/calculus/context';
import { UploadCustomerMarkupDialog } from './UploadCustomerMarkupDialog';
import { EditCustomerMarkupDialog } from './EditCustomerMarkupDialog';

interface Props {
  conditions: ConditionsNew[];
  isFetching: boolean;
  supplierNumber?: number;
}

export function CustomerMarkupContainer({ conditions, isFetching, supplierNumber }: Props) {
  const t = useCalculusTFunction();
  const { isPriceListContext, selectedOrgUnitAssortment } = useSelectedOrgUnit();
  const { removeCondition } = useConditionActions();

  const { userCanCreate, userCanUpdate } = usePermissionsForCurrentUser();
  const userCanCreateCustomerMarkupsConditions = userCanCreate(
    'PRICE_CALCULATION__CUSTOMER_MARKUP'
  );
  const userCanUpdateCustomerMarkupsConditions = userCanUpdate(
    'PRICE_CALCULATION__CUSTOMER_MARKUP'
  );

  const { isOpen: isUploadOpen, onToggle: toggleUploadDialog } = useDisclosure();
  const { isOpen: isCreateOpen, onToggle: _toggleCreateDialog } = useDisclosure();
  const toggleCreateDialog = userCanCreateCustomerMarkupsConditions
    ? _toggleCreateDialog
    : undefined;

  const [conditionIdForDeactivation, setConditionIdForDeactivation] = useState<
    number | undefined
  >();
  const [customerMarkupForEdit, setCustomerMarkupForEdit] = useState<ConditionsNew | undefined>();

  return (
    <Stack flexGrow={1}>
      <CalculusActionPortal>
        <Stack flexDirection="row" alignItems="center" gap={1}>
          {supplierNumber && (
            <OutlinedButton size="small" color="secondary" onClick={toggleUploadDialog}>
              {t('calculus.customerMarkup.upload')}
            </OutlinedButton>
          )}
          <AddButton onClick={toggleCreateDialog}>{t('calculus.createCustomerMarkup')}</AddButton>
        </Stack>
      </CalculusActionPortal>

      <CustomerMarkupTable
        conditions={conditions}
        loading={isFetching}
        multipleChains={!isPriceListContext}
        supplierLevel={!!supplierNumber}
        openCreateDialog={toggleCreateDialog}
        handleDeactivate={setConditionIdForDeactivation}
        handleEdit={setCustomerMarkupForEdit}
      />

      {isCreateOpen && toggleCreateDialog && (
        <CreateCustomerMarkupConditionDialog
          open
          target={supplierNumber ? { type: 'supplier', supplierNumber } : { type: 'assortment' }}
          onClose={toggleCreateDialog}
        />
      )}

      {userCanUpdateCustomerMarkupsConditions && (
        <>
          {conditionIdForDeactivation && (
            <DeleteDialog
              isOpen
              dialogTitle={t('calculus.actions.deleteFromDraft')}
              message={t('calculus.actions.confirmRemoval')}
              onDelete={() => removeCondition([conditionIdForDeactivation], 'deactivate')}
              onClose={() => setConditionIdForDeactivation(undefined)}
            />
          )}
          {customerMarkupForEdit && setCustomerMarkupForEdit && (
            <EditCustomerMarkupDialog
              open
              customerMarkupToEdit={customerMarkupForEdit}
              assortmentId={customerMarkupForEdit.assortmentId}
              onClose={() => setCustomerMarkupForEdit(undefined)}
            />
          )}
        </>
      )}
      {isUploadOpen && supplierNumber && (
        <UploadCustomerMarkupDialog
          open
          onClose={toggleUploadDialog}
          supplierNumber={supplierNumber}
          priceListAssortmentId={
            isPriceListContext ? selectedOrgUnitAssortment.draftAssortmentId : undefined
          }
        />
      )}
    </Stack>
  );
}
